import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Youtube, Instagram, Facebook, Twitter } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_cf4caf13-b586-4cda-9c41-d59090cfa577/artifacts/jj63lgza_Logo%20Firma_White%20logo.png";
const SPOTIFY_URL = "https://open.spotify.com/artist/4mVbIGHmeO5AcP5TrLk2V2";

export const Footer = () => {
  const { t } = useLanguage();

  const socialLinks = [
    { icon: Youtube, href: 'https://www.youtube.com/sfrodelly', label: 'YouTube' },
    { icon: Instagram, href: 'https://www.instagram.com/sarah.capeles/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/sarah.capeles', label: 'Facebook' },
    { icon: Twitter, href: 'https://www.twitter.com/sarahcapeles', label: 'Twitter' },
  ];

  return (
    <footer className="bg-charcoal text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src={LOGO_WHITE} alt="Sarah Capeles" className="h-12 w-auto" />
            </Link>
            <p className="font-lato text-gray-400 text-sm leading-relaxed mb-6">
              {t(
                'Inspirational Christian Music That Lifts You Up To The Heavenly Throne.',
                'Música Cristiana Inspiradora Que Eleva Al Trono Celestial.'
              )}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold-500 transition-colors duration-300"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-4 text-gold-400">
              {t('Quick Links', 'Enlaces Rápidos')}
            </h4>
            <ul className="space-y-3">
              <li><Link to="/" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('Home', 'Inicio')}</Link></li>
              <li><Link to="/music" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('Music', 'Música')}</Link></li>
              <li><Link to="/soundtracks" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('Soundtracks', 'Pistas')}</Link></li>
              <li><Link to="/videos" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('Videos', 'Videos')}</Link></li>
              <li><Link to="/about" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('About', 'Acerca de')}</Link></li>
              <li><Link to="/contact" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">{t('Contact', 'Contacto')}</Link></li>
            </ul>
          </div>

          {/* My Music */}
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-4 text-gold-400">
              {t('My Music', 'Mi Música')}
            </h4>
            <ul className="space-y-3">
              <li><a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">Spotify</a></li>
              <li><a href="https://music.apple.com/us/artist/sarah-capeles/858107097" target="_blank" rel="noopener noreferrer" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">Apple Music</a></li>
              <li><a href="https://www.youtube.com/sfrodelly" target="_blank" rel="noopener noreferrer" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">YouTube</a></li>
              <li><a href="https://www.tiktok.com/@sarahcapeles" target="_blank" rel="noopener noreferrer" className="font-lato text-gray-400 hover:text-gold-400 transition-colors">TikTok</a></li>
            </ul>
          </div>

          {/* Scripture & Sabbath */}
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-4 text-gold-400">
              {t('Scripture', 'Escritura')}
            </h4>
            <blockquote className="font-lato text-gray-400 text-sm italic leading-relaxed border-l-2 border-gold-500 pl-4">
              {t(
                '"Make a joyful noise unto the Lord, all the earth: make a loud noise, and rejoice, and sing praise." — Psalm 98:4',
                '"Cantad alegres a Dios, habitantes de toda la tierra; levantad la voz, y aplaudid, y cantad salmos." — Salmo 98:4'
              )}
            </blockquote>
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="font-lato text-xs text-gold-400 uppercase tracking-widest mb-2">
                {t('Remember the Sabbath', 'Acuérdate del Sábado')}
              </p>
              <p className="font-lato text-gray-400 text-sm">
                {t(
                  'Seventh-day Adventist Singer',
                  'Ministerio Adventista del Séptimo Día'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <p className="font-lato text-xs uppercase tracking-[0.3em] text-gold-400 mb-2">
                {t('Stay Connected', 'Mantente Conectado')}
              </p>
              <h4 className="font-playfair text-2xl font-bold text-white">
                {t(
                  'Be the first to hear new releases',
                  'Entérate primero de los nuevos lanzamientos'
                )}
              </h4>
            </div>
            <div className="md:w-96">
              <NewsletterSignup />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-lato text-gray-500 text-sm">
              © {new Date().getFullYear()} Sarah Capeles. {t('All rights reserved.', 'Todos los derechos reservados.')}
            </p>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <span>{t('Made with', 'Hecho con')}</span>
              <Heart className="w-4 h-4 text-gold-500 fill-gold-500" />
              <span>{t('for the Glory of God', 'para la Gloria de Dios')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
